import React, { useMemo, useState, useEffect } from 'react';
import { notifyError } from '../lib/notify';
import { Link } from 'react-router-dom';
import axios from '../../config/axios';
import { PageTitle, Rule, Meta, Display, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { Sprout } from '../components/Illustrations';
import { LoadingState, ErrorState, EmptyState, Skeleton, TodaySkeleton } from '../components/states';
import { useMultipleApi, useApi, formatDate, daysSince, plantStage, taskTypeMeta, categoryTone } from '../lib/api';

/**
 * Today screen - dashboard editorial Terroir z PRAWDZIWYMI danymi
 * konta zalogowanego (auth przez httpOnly cookie z produkcji).
 *
 * Endpoints:
 *   /api/auth/me           - user info
 *   /api/tasks?limit=10    - dzisiejsze + najbliższe zadania
 *   /api/plots             - lista działek (do spotlight)
 *   /api/weather/current   - bieżąca pogoda + lokalizacja
 *   /api/plots/all-with-details - wszystkie beds (do plant spotlights)
 */

const TONE_COLOR = {
  sage:  'var(--t-sage)',
  rust:  'var(--t-rust)',
  amber: 'var(--t-amber)',
  dim:   'var(--t-text-mute)',
};

const ICON_BY_NAME = {
  sprout:   Icons.Sprout,
  scissors: Icons.Scissors,
  drop:     Icons.Drop,
  sun:      Icons.Sun,
};

// ── helpers ──────────────────────────────────────────────────────
function formatToday() {
  const d = new Date();
  const days = ['NIE', 'PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB'];
  const months = ['STY', 'LUT', 'MAR', 'KWI', 'MAJ', 'CZE', 'LIP', 'SIE', 'WRZ', 'PAŹ', 'LIS', 'GRU'];
  // Number tygodnia ISO
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  const week = 1 + Math.ceil((firstThursday - target) / 604800000);
  return `${days[d.getDay()]} · ${d.getDate()} ${months[d.getMonth()]} · TYDZ. ${week}`;
}

function userInitials(user) {
  if (!user) return '?';
  const src = user.public_display_name || user.username || user.email || '';
  return src.trim().slice(0, 2).toUpperCase();
}

// ── Task card ────────────────────────────────────────────────────
function TaskCard({ task, num, onComplete }) {
  const meta = taskTypeMeta(task.task_type);
  const Icon = ICON_BY_NAME[meta.iconName] || Icons.Sun;
  const color = TONE_COLOR[meta.tone];
  const due = formatDate(task.due_date, 'relative');
  const isToday = daysSince(task.due_date) === 0;
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const handleComplete = async (e) => {
    e?.stopPropagation();
    if (completing || completed) return;
    setCompleting(true);
    try {
      await axios.post(`/api/tasks/${task.id}/complete`);
      setCompleted(true);
      // Krótki delay żeby user zobaczył checkmark, potem refresh
      setTimeout(() => onComplete?.(task.id), 380);
    } catch (err) {
      setCompleting(false);
      const msg = err.response?.data?.error || 'Nie udało się oznaczyć zadania.';
      notifyError(msg);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: 16, borderRadius: 14,
      background: 'var(--t-bg-elev)',
      border: `1px solid ${completed ? 'var(--t-sage)' : isToday ? 'var(--t-line-2)' : 'var(--t-line)'}`,
      transition: 'border-color 200ms ease, transform 200ms ease, opacity 320ms ease',
      opacity: completed ? 0.5 : 1,
    }}
    onMouseEnter={e => {
      if (completed) return;
      e.currentTarget.style.borderColor = 'var(--t-line-2)';
      e.currentTarget.style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={e => {
      if (completed) return;
      e.currentTarget.style.borderColor = isToday ? 'var(--t-line-2)' : 'var(--t-line)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: 'var(--t-dim-bg)',
        display: 'grid', placeItems: 'center', position: 'relative',
      }}>
        <Icon color={color} size={18} />
        <span style={{
          position: 'absolute', top: -6, right: -6,
          fontFamily: 'var(--t-mono)', fontSize: 8,
          letterSpacing: '.1em', color: 'var(--t-text-mute)',
        }}>{String(num).padStart(2, '0')}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 16, fontWeight: 500,
          color: 'var(--t-text)', marginBottom: 3,
          letterSpacing: '-.01em',
          textDecoration: completed ? 'line-through' : 'none',
        }}>{task.title || task.description}</div>
        {(task.plant_name || task.plot_name) && (
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 6 }}>
            {[task.plot_name, task.plant_name].filter(Boolean).join(' · ')}
          </div>
        )}
        <Meta>{due}{task.priority === 3 || task.priority === 'high' ? ' · WAŻNE' : ''}</Meta>
      </div>
      <button
        type="button"
        onClick={handleComplete}
        disabled={completing || completed}
        aria-label={completed ? 'Wykonane' : 'Oznacz jako zrobione'}
        style={{
          width: 28, height: 28, borderRadius: 7,
          background: completed ? 'var(--t-sage)' : 'transparent',
          border: `1.2px solid ${completed ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
          flexShrink: 0,
          cursor: completed ? 'default' : 'pointer',
          padding: 0,
          color: 'var(--t-bg)',
          fontSize: 14, lineHeight: 1, fontWeight: 600,
          display: 'grid', placeItems: 'center',
          transition: 'background 200ms ease, border-color 200ms ease, transform 200ms ease',
          transform: completing && !completed ? 'scale(0.92)' : 'scale(1)',
        }}
        onMouseEnter={e => {
          if (completed || completing) return;
          e.currentTarget.style.borderColor = 'var(--t-sage)';
        }}
        onMouseLeave={e => {
          if (completed || completing) return;
          e.currentTarget.style.borderColor = 'var(--t-line-2)';
        }}
      >{completed ? '✓' : ''}</button>
    </div>
  );
}

// ── Plant spotlight ──────────────────────────────────────────────
function PlantSpotlight({ bed, compact }) {
  const stage = plantStage(bed);
  const tone = stage.tone;
  const color = TONE_COLOR[tone] || TONE_COLOR.sage;

  if (compact) {
    return (
      <div style={{ padding: '14px 0', borderBottom: '1px solid var(--t-line)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div>
            <Display size={20} style={{ display: 'block', letterSpacing: '-.02em' }}>{bed.plant_name}</Display>
            <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3 }}>
              {bed.plant_variety || '—'}
            </div>
          </div>
          {stage.d != null && (
            <Display size={22} style={{ color, fontWeight: 300 }}>d{stage.d}</Display>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <Meta color={tone}>{stage.stage}</Meta>
          {bed.plot_name && (
            <span style={{
              fontSize: 11, color: 'var(--t-text-mute)',
              fontFamily: 'var(--t-mono)', letterSpacing: '.1em',
            }}>{bed.plot_name}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: 168, flexShrink: 0,
      borderRadius: 16, overflow: 'hidden',
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
    }}>
      <div style={{
        height: 108,
        background: `linear-gradient(135deg, ${color}22, ${color}08)`,
        display: 'grid', placeItems: 'center',
        borderBottom: '1px solid var(--t-line)',
      }}>
        <Sprout size={56} color={color} />
      </div>
      <div style={{ padding: '12px 14px' }}>
        <Display size={20} style={{ display: 'block', letterSpacing: '-.02em' }}>{bed.plant_name}</Display>
        <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 2 }}>
          {bed.plant_variety || '—'}
        </div>
        <div style={{
          marginTop: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <Meta color={tone}>{stage.stage}</Meta>
          {stage.d != null && <Display size={18}>d{stage.d}</Display>}
        </div>
      </div>
    </div>
  );
}

// ── EmailVerificationBanner (gdy user nie zweryfikował emaila) ──
function EmailVerificationBanner({ user, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;

  const handleResend = async (e) => {
    e?.stopPropagation();
    if (busy || sent) return;
    setBusy(true);
    try {
      await axios.post('/api/auth/resend-verification');
      setSent(true);
    } catch (err) {
      notifyError('Nie udało się wysłać: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      padding: '14px 18px',
      background: 'rgba(220, 180, 100, 0.10)',
      borderTop: '1px solid var(--t-amber)',
      borderBottom: '1px solid var(--t-amber)',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, color: 'var(--t-amber)' }}>✉</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-amber)', marginBottom: 3,
        }}>Email niezweryfikowany</div>
        <div style={{ fontSize: 13, color: 'var(--t-text)', lineHeight: 1.4 }}>
          {sent
            ? `✓ Wysłano nowy link na ${user.email}. Sprawdź skrzynkę (też SPAM).`
            : `Sprawdź skrzynkę ${user.email}, kliknij link żeby aktywować konto.`}
        </div>
      </div>
      {!sent && (
        <button
          type="button"
          onClick={handleResend}
          disabled={busy}
          style={{
            background: 'transparent', border: '1px solid var(--t-amber)',
            color: 'var(--t-amber)',
            padding: '6px 12px', borderRadius: 6,
            cursor: busy ? 'wait' : 'pointer',
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.16em', textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >{busy ? '…' : 'Wyślij ponownie'}</button>
      )}
    </div>
  );
}

// ── WelcomeCard (1 raz po onboardingu) ───────────────────────────
function WelcomeCard({ user, onDismiss }) {
  const [dismissing, setDismissing] = useState(false);
  if (!user || user.welcome_card_dismissed || !user.onboarding_completed) return null;

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await axios.put('/api/auth/dismiss-welcome');
      onDismiss?.();
    } catch (err) {
      // Ignore — i tak ukrywamy lokalnie
      onDismiss?.();
    }
  };

  const name = user.public_display_name || user.username || 'ogrodniku';

  return (
    <div style={{
      margin: '0 24px 18px',
      padding: '20px 22px', borderRadius: 14,
      background: 'linear-gradient(135deg, rgba(168, 196, 158, 0.10) 0%, rgba(220, 180, 100, 0.06) 100%)',
      border: '1px solid var(--t-sage)',
      position: 'relative',
    }}>
      <button
        type="button"
        onClick={handleDismiss}
        disabled={dismissing}
        aria-label="Zamknij"
        style={{
          position: 'absolute', top: 10, right: 10,
          background: 'transparent', border: 'none',
          color: 'var(--t-text-mute)', cursor: 'pointer',
          fontSize: 20, lineHeight: 1, padding: 4,
        }}
      >×</button>
      <Meta color="sage" style={{ marginBottom: 8 }}>Witaj w GardenApp.pl</Meta>
      <Display size={20} style={{
        display: 'block', letterSpacing: '-.02em', marginBottom: 10,
        paddingRight: 30, wordBreak: 'break-word',
      }}>Cieszymy się, że jesteś, {name}.</Display>
      <div style={{
        fontSize: 13, color: 'var(--t-text-dim)',
        lineHeight: 1.55, marginBottom: 14,
      }}>
        Zacznij od dodania pierwszej działki w sekcji <Link to="/plots" style={{ color: 'var(--t-sage)' }}>Działki</Link>{' '}
        albo przejrzyj 1100+ roślin w <Link to="/plants" style={{ color: 'var(--t-sage)' }}>Katalogu</Link>.
        {' '}Pogoda i alerty mrozowe pojawią się pod tym pasem, gdy uzupełnisz lokalizację w{' '}
        <Link to="/profile/garden" style={{ color: 'var(--t-sage)' }}>Mój ogród</Link>.
      </div>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.16em', textTransform: 'uppercase',
        color: 'var(--t-text-mute)',
      }}>Możesz schować to za pomocą × w prawym górnym rogu.</div>
    </div>
  );
}

// ── WhatToPlant widget (T5) — co posadzić teraz ─────────────────
function WhatToPlantWidget() {
  const { data, loading, error } = useApi('/api/planner/what-to-plant');

  if (loading) return <Skeleton height={80} />;
  if (error || !data || !data.groups || data.groups.length === 0) return null;

  const firstGroup = data.groups[0];
  const totalOthers = data.groups.slice(1).reduce((sum, g) => sum + g.items.length, 0);

  return (
    <Link to="/co-posadzic" style={{ textDecoration: 'none', display: 'block' }}>
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        background: 'rgba(168, 196, 158, 0.08)',
        border: '1px solid rgba(168, 196, 158, 0.20)',
        marginBottom: 16,
      }}>
        <Meta color="sage" style={{ marginBottom: 6 }}>Na teraz · {data.month}. miesiąc</Meta>
        <Display size={16} style={{
          display: 'block', letterSpacing: '-.01em', lineHeight: 1.35,
        }}>
          {firstGroup.methodIcon} {firstGroup.items.length} {firstGroup.methodLabel}
          {firstGroup.items.length <= 2
            ? ': ' + firstGroup.items.map(i => i.plant.slice(0, 20)).join(', ')
            : ` — ${firstGroup.items.length} roślin`}
          {totalOthers > 0 && ` + ${totalOthers} inne`}
        </Display>
        <div style={{
          marginTop: 4, fontSize: 11, color: 'var(--t-text-dim)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          {data.lastFrostPassed
            ? 'Po przymrozkach — śmiało siej do gruntu'
            : '⚠ Uwaga na przymrozki — niektóre rośliny wymagają ochrony'}
        </div>
      </div>
    </Link>
  );
}

// ── Karencja banner (sticky na górze gdy aktywny oprysk) ────────
function WithdrawalBanner({ reminders }) {
  if (!Array.isArray(reminders)) return null;
  const today = new Date().toISOString().split('T')[0];
  // Karencje to remindery z safe_harvest_date >= today
  const active = reminders.filter(r =>
    r.safe_harvest_date && r.safe_harvest_date >= today
  );
  if (active.length === 0) return null;

  // Najwcześniej wygasająca karencja
  const sorted = [...active].sort((a, b) =>
    a.safe_harvest_date.localeCompare(b.safe_harvest_date)
  );
  const next = sorted[0];
  const days = Math.max(0, Math.round(
    (new Date(next.safe_harvest_date) - new Date(today)) / 86400000
  ));

  return (
    <Link to="/care" style={{ textDecoration: 'none' }}>
      <div style={{
        padding: '12px 16px',
        background: 'rgba(192, 106, 74, 0.10)',
        borderBottom: '1px solid var(--t-rust)',
        borderTop: '1px solid var(--t-rust)',
        display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer',
      }}>
        <div style={{
          fontSize: 18, lineHeight: 1, flexShrink: 0,
          color: 'var(--t-rust)',
        }}>⚠</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-rust)', marginBottom: 3,
          }}>Karencja oprysku</div>
          <div style={{ fontSize: 13, color: 'var(--t-text)', lineHeight: 1.4 }}>
            <strong>{next.spray_name || next.title || 'Aktywny oprysk'}</strong>
            {next.plant_name && ` · ${next.plant_name}`}
            {' '}— bezpieczny zbiór za {days === 0 ? 'dziś' : `${days} ${days === 1 ? 'dzień' : 'dni'}`}
            {active.length > 1 && (
              <span style={{ color: 'var(--t-text-mute)' }}> · +{active.length - 1} innych</span>
            )}
          </div>
        </div>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', color: 'var(--t-rust)',
          flexShrink: 0,
        }}>SZCZEGÓŁY →</div>
      </div>
    </Link>
  );
}

// ── Weather alert banner (mróz / upał / silny wiatr) ────────────
function WeatherAlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  // Pokaż tylko najpilniejszy (pierwszy)
  const a = alerts[0];
  const icon = a.type?.includes('frost') ? '❄' :
               a.type?.includes('heat') ? '🔥' :
               a.type?.includes('wind') ? '💨' :
               a.type?.includes('rain') || a.type?.includes('storm') ? '🌧' : '⚠';
  const tone = a.severity === 'critical' || a.type?.includes('frost') ? 'rust' :
               a.severity === 'warning' ? 'amber' : 'sage';
  const color = tone === 'rust' ? 'var(--t-rust)' :
                tone === 'amber' ? 'var(--t-amber)' : 'var(--t-sage)';
  const bg = tone === 'rust' ? 'rgba(192, 106, 74, 0.10)' :
             tone === 'amber' ? 'rgba(220, 180, 100, 0.10)' :
             'rgba(168, 196, 158, 0.08)';

  return (
    <Link to="/pogoda" style={{ textDecoration: 'none' }}>
      <div style={{
        padding: '12px 14px', borderRadius: 12,
        background: bg, borderLeft: `3px solid ${color}`,
        display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer',
      }}>
        <div style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, color: 'var(--t-text)', fontWeight: 500,
            letterSpacing: '-.01em',
          }}>{a.title || a.message}</div>
          {alerts.length > 1 && (
            <div style={{
              fontSize: 11, color: 'var(--t-text-mute)',
              fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
              marginTop: 2,
            }}>+ {alerts.length - 1} innych ostrzeżeń</div>
          )}
        </div>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', color: color,
          flexShrink: 0,
        }}>SZCZEGÓŁY →</div>
      </div>
    </Link>
  );
}

// ── Weather block ────────────────────────────────────────────────
function WeatherBlock({ weather, compact }) {
  if (!weather?.weather) return null;
  const w = weather.weather;
  const city = weather.location?.city || 'Twoja lokalizacja';
  const temp = Math.round(w.temperature);
  const willRain = w.rain > 0 || /deszcz|burz|opady/i.test(w.description || '');
  const advice = willRain ? 'Nie\npodlewaj' : null;

  if (compact) {
    return (
      <div style={{
        padding: '16px 18px', borderRadius: 16,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        display: 'flex', gap: 14, alignItems: 'center',
      }}>
        <Display size={42}>{temp}°</Display>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--t-text)', marginBottom: 2, textTransform: 'capitalize' }}>
            {w.description}
          </div>
          <Meta>Wilg. {w.humidity}% · Wiatr {Math.round(w.windSpeed)} km/h</Meta>
        </div>
        {advice && (
          <div style={{
            padding: '6px 10px', borderRadius: 8,
            background: 'var(--t-amber-bg)', color: 'var(--t-amber)',
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 500,
            textAlign: 'center', lineHeight: 1.3,
            whiteSpace: 'pre-line',
          }}>{advice}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <Meta style={{ marginBottom: 12 }}>Pogoda · {city}</Meta>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <Display size={72} style={{ letterSpacing: '-.03em' }}>{temp}°</Display>
        <div>
          <div style={{ fontSize: 13, color: 'var(--t-text)', textTransform: 'capitalize' }}>{w.description}</div>
          <Meta color={advice ? 'amber' : 'dim'} style={{ marginTop: 2 }}>
            odczuwalnie {Math.round(w.feelsLike)}°
          </Meta>
        </div>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 14,
        padding: '12px 0',
        borderTop: '1px solid var(--t-line-2)',
        borderBottom: '1px solid var(--t-line-2)',
      }}>
        {[['Wilg.', `${w.humidity}%`], ['Wiatr', `${Math.round(w.windSpeed)} km/h`], ['Opady', `${w.rain ?? 0} mm`]].map(([lab, val], i) => (
          <div key={i}>
            <div style={{
              fontFamily: 'var(--t-mono)', fontSize: 8,
              letterSpacing: '.2em', color: 'var(--t-text-mute)',
              textTransform: 'uppercase', marginBottom: 3,
            }}>{lab}</div>
            <Display size={16}>{val}</Display>
          </div>
        ))}
      </div>
      {advice && (
        <div style={{
          marginTop: 14, padding: '10px 14px',
          background: 'rgba(220, 180, 100, 0.1)',
          borderLeft: '2px solid var(--t-amber)',
          fontSize: 12, color: 'var(--t-text)', lineHeight: 1.5,
        }}>
          Opady przewidziane — możesz odpuścić podlewanie.
        </div>
      )}
    </div>
  );
}

// ── Helpers do data ──────────────────────────────────────────────
function flattenBeds(plots) {
  if (!Array.isArray(plots)) return [];
  return plots.flatMap(p =>
    (p.beds || []).map(b => ({ ...b, plot_name: p.name }))
  );
}

function pickSpotlights(beds, limit = 3) {
  if (!beds.length) return [];
  // Priorytet: pomidor / ważne / najwcześniej do zbioru
  const sorted = [...beds].sort((a, b) => {
    const stageA = plantStage(a);
    const stageB = plantStage(b);
    if (stageA.tone === 'rust' && stageB.tone !== 'rust') return -1;
    if (stageB.tone === 'rust' && stageA.tone !== 'rust') return 1;
    if (stageA.tone === 'amber' && stageB.tone === 'sage') return -1;
    if (stageB.tone === 'amber' && stageA.tone === 'sage') return 1;
    return 0;
  });
  return sorted.slice(0, limit);
}

function tasksForToday(tasks) {
  if (!Array.isArray(tasks)) return { today: [], upcoming: [] };
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const out = { today: [], upcoming: [] };
  tasks.forEach(t => {
    if (t.completed) return;
    const d = new Date(t.due_date);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === todayDate.getTime()) out.today.push(t);
    else if (d > todayDate) out.upcoming.push(t);
  });
  return out;
}

// ── Mobile content ──────────────────────────────────────────────
// ── UpcomingHarvests — najbliższe zbiory (na podstawie expected_harvest_date) ──
// ── SeasonalActions — sezonowe podpowiedzi z data/seasonalHints.js (M21) ──
// Pokazuje 3-5 akcji na obecny miesiąc z linkami do odpowiednich ekranów.
function SeasonalActionsBlock({ mobile }) {
  // Lazy require żeby nie ładować w innych miejscach
  let hints;
  try {
    hints = require('../../data/seasonalHints').seasonalHints;
  } catch {
    return null;
  }
  const month = new Date().getMonth() + 1; // 1-12
  const data = hints[month];
  if (!data) return null;

  // Łączymy wszystkie 3 sekcje (vegetables/flowers/general) i bierzemy max 5
  const actions = [
    ...(data.vegetables || []).map(t => ({ text: t, group: 'vegetables', tone: 'sage', icon: '🥕' })),
    ...(data.flowers || []).map(t => ({ text: t, group: 'flowers', tone: 'rust', icon: '🌸' })),
    ...(data.general || []).map(t => ({ text: t, group: 'general', tone: 'amber', icon: '🌿' })),
  ].slice(0, mobile ? 4 : 6);

  if (actions.length === 0) return null;

  // Link do odpowiedniej sekcji aplikacji per akcja (heurystyka po słowach kluczowych)
  const linkFor = (text) => {
    const t = text.toLowerCase();
    if (/oprysk|opryskuj|miedzi|mydl|grzyb|szkodni/.test(t))   return { to: '/care', label: 'Pielęgnacja' };
    if (/nawożeni|nawóz|kompost|obornik/.test(t))              return { to: '/care/fertilization', label: 'Nawożenie' };
    if (/kwitn|kwiat.*ciąć|przekwit|deadhead/.test(t))         return { to: '/garden-tasks', label: 'Kwitnienie' };
    if (/sukces|kolejna tura|drugi siew|trzeci siew/.test(t))  return { to: '/garden-tasks/routine', label: 'Sukcesja' };
    if (/zima|okryć|okrycie|stroisz|propag|dziel.*byliny/.test(t)) return { to: '/garden-tasks/seasonal', label: 'Sezon' };
    if (/zbiór|zbiera|zbieraj|zbiory/.test(t))                 return { to: '/plots', label: 'Działki' };
    if (/siew|wysiew|sadzenie|sadzeni|posadz/.test(t))         return { to: '/garden-planner', label: 'Planer' };
    if (/podlewa|podlej|nawodn/.test(t))                       return { to: '/tasks', label: 'Zadania' };
    if (/planuj|plan/.test(t))                                 return { to: '/garden-planner', label: 'Planer' };
    return null;
  };

  return (
    <div style={{
      padding: mobile ? '14px 16px' : '16px 18px',
      margin: mobile ? '0 24px 16px' : '0 0 22px',
      borderRadius: 14,
      background: 'rgba(168, 196, 158, 0.06)',
      border: '1px solid rgba(168, 196, 158, 0.20)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 10, marginBottom: 12,
      }}>
        <Meta color="sage">{data.icon} Co teraz w ogrodzie · {data.month}</Meta>
        <Link to="/garden-tasks" style={{ textDecoration: 'none' }}>
          <Meta color="sage" style={{ cursor: 'pointer' }}>→</Meta>
        </Link>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {actions.map((a, i) => {
          const link = linkFor(a.text);
          const accent = a.tone === 'sage' ? 'var(--t-sage)' :
                         a.tone === 'rust' ? 'var(--t-rust)' :
                         'var(--t-amber)';
          const content = (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px 12px', borderRadius: 10,
              background: 'rgba(232, 230, 221, 0.025)',
              borderLeft: `3px solid ${accent}`,
              cursor: link ? 'pointer' : 'default',
              transition: 'background 200ms ease',
            }}
            onMouseEnter={e => { if (link) e.currentTarget.style.background = 'rgba(232, 230, 221, 0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(232, 230, 221, 0.025)'; }}
            >
              <span style={{ fontSize: 16, lineHeight: 1.2, flexShrink: 0 }}>{a.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, color: 'var(--t-text)',
                  letterSpacing: '-.01em', lineHeight: 1.45,
                }}>{a.text}</div>
                {link && (
                  <div style={{
                    marginTop: 4,
                    fontFamily: 'var(--t-mono)', fontSize: 10,
                    letterSpacing: '.18em', textTransform: 'uppercase',
                    color: accent,
                  }}>→ {link.label}</div>
                )}
              </div>
            </div>
          );
          return link ? (
            <Link key={i} to={link.to} style={{ textDecoration: 'none' }}>
              {content}
            </Link>
          ) : (
            <div key={i}>{content}</div>
          );
        })}
      </div>
      <div style={{
        marginTop: 10, paddingTop: 10,
        borderTop: '1px solid var(--t-line)',
        display: 'flex', justifyContent: 'space-between',
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-text-mute)',
      }}>
        <span>{data.priority === 'high' ? '● Sezon szczytowy' : data.priority === 'medium' ? '● Sezon przejściowy' : '● Pora przerwy'}</span>
        <Link to="/garden-tasks" style={{ color: 'var(--t-sage)', textDecoration: 'none' }}>
          Pełen harmonogram →
        </Link>
      </div>
    </div>
  );
}

function UpcomingHarvestsBlock({ harvests, mobile }) {
  // Filter: pokazuj tylko PRZYSZŁE zbiory (days_until_harvest >= 0).
  // Backend mógł zwrócić też przeszłe (jeśli expected_harvest_date < dziś
  // i nie odznaczono actual_harvest_date), ale "Najbliższe zbiory"
  // ma znaczyć: co dojrzeje wkrótce, nie historia.
  const upcoming = (harvests || []).filter(h => (h.days_until_harvest ?? 0) >= 0);
  const items = upcoming.slice(0, mobile ? 3 : 5);
  if (items.length === 0) return null;
  return (
    <div style={{
      padding: mobile ? '14px 16px' : '16px 18px',
      margin: mobile ? '0 24px 16px' : '0 0 22px',
      borderRadius: 14,
      background: 'rgba(220, 180, 100, 0.08)',
      border: '1px solid rgba(220, 180, 100, 0.20)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 10, marginBottom: 10,
      }}>
        <Meta color="amber">🥕 Najbliższe zbiory</Meta>
        <Link to="/plots" style={{ textDecoration: 'none' }}>
          <Meta color="amber" style={{ cursor: 'pointer' }}>→</Meta>
        </Link>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(h => {
          const days = h.days_until_harvest;
          const dueLabel = days === 0 ? 'DZIŚ' : days === 1 ? 'JUTRO' : days < 0 ? `${Math.abs(days)} dni temu` : `za ${days} dni`;
          const dueColor = days <= 0 ? 'var(--t-rust)' : days <= 7 ? 'var(--t-amber)' : 'var(--t-text-dim)';
          return (
            <Link
              key={h.id}
              to={`/plots/${h.plot_id}`}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, padding: '8px 10px', borderRadius: 8,
                textDecoration: 'none',
                background: 'rgba(220, 180, 100, 0.04)',
                border: '1px solid rgba(220, 180, 100, 0.15)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--t-text)', letterSpacing: '-.01em' }}>
                  {h.plant_name || 'Roślina'}{h.plant_variety ? ` · ${h.plant_variety}` : ''}
                </div>
                <div style={{
                  fontSize: 10, color: 'var(--t-text-mute)',
                  fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                  textTransform: 'uppercase',
                }}>{h.plot_name} · rząd {h.row_number || '—'}</div>
              </div>
              <div style={{
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.18em', textTransform: 'uppercase',
                color: dueColor, fontWeight: 600,
                whiteSpace: 'nowrap',
              }}>{dueLabel}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function MobileContent({ data, user }) {
  const beds = flattenBeds(data.plots);
  const spotlights = pickSpotlights(beds);
  const { today, upcoming } = tasksForToday(data.tasks?.data || data.tasks);
  const visibleTasks = today.length > 0 ? today : upcoming.slice(0, 3);
  const taskCount = today.length;

  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <EmailVerificationBanner user={user} />
      <WithdrawalBanner reminders={data.reminders} />
      {!welcomeDismissed && (
        <div style={{ paddingTop: 18 }}>
          <WelcomeCard user={user} onDismiss={() => setWelcomeDismissed(true)} />
        </div>
      )}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>{formatToday()}</Meta>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--t-sage)', color: 'var(--t-bg)',
          display: 'grid', placeItems: 'center',
          fontSize: 11, fontWeight: 600,
        }}>{userInitials(user)}</div>
      </div>

      <PageTitle
        eyebrow="Dziś w ogrodzie"
        title={taskCount === 0 ? 'Dzień bez zadań.' : taskCount === 1 ? 'Jedna rzecz dla ciebie.' : `${taskCount === 2 ? 'Dwie' : taskCount === 3 ? 'Trzy' : taskCount} ${taskCount < 5 ? 'rzeczy' : 'rzeczy'} dla ciebie.`}
        sub={data.weather?.weather && /deszcz|burz|opady/i.test(data.weather.weather.description || '')
          ? 'Opady wieczorem — podlewanie możesz odpuścić.'
          : 'Sprawdź pogodę i zacznij od najważniejszej rzeczy.'}
      />

      <div style={{ margin: '0 24px 16px' }}>
        {data.weather ? (
          <Link to="/pogoda" style={{ textDecoration: 'none', display: 'block' }}>
            <WeatherBlock weather={data.weather} compact />
          </Link>
        ) : <Skeleton height={80} />}
        {data.weatherRecs?.recommendations?.alerts?.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <WeatherAlertBanner alerts={data.weatherRecs.recommendations.alerts} />
          </div>
        )}
      </div>

      <div style={{ margin: '0 24px 16px' }}>
        <WhatToPlantWidget />
      </div>

      <UpcomingHarvestsBlock harvests={data.upcomingHarvests} mobile />

      <SeasonalActionsBlock mobile />

      <div style={{
        padding: '0 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 14,
      }}>
        <Meta>Zadania{visibleTasks.length > 0 ? ` · ${visibleTasks.length}` : ''}</Meta>
        <Link to="/reminders" style={{ textDecoration: 'none' }}>
          <Meta color="sage" style={{ cursor: 'pointer' }}>Wszystkie →</Meta>
        </Link>
      </div>

      {visibleTasks.length > 0 ? (
        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleTasks.map((t, i) => <TaskCard key={t.id} task={t} num={i + 1} onComplete={data.refetchAll} />)}
        </div>
      ) : (
        <div style={{ padding: '0 24px' }}>
          <EmptyState
            illustration="bed"
            title="Brak zadań na dziś"
            description="Skorzystaj z chwili, żeby przejść się po ogrodzie i zaplanować coś nowego."
          />
        </div>
      )}

      {spotlights.length > 0 && (
        <>
          <Rule label="Obserwuj" />
          <div style={{
            padding: '0 24px',
            position: 'relative',
            // Wskazówka scroll-w-bok: gradient fade po prawej krawędzi
            // sygnalizuje że karty się ciągną poza viewport.
            WebkitMaskImage: 'linear-gradient(to right, black 0, black calc(100% - 28px), transparent 100%)',
            maskImage: 'linear-gradient(to right, black 0, black calc(100% - 28px), transparent 100%)',
          }}>
            <div style={{
              display: 'flex',
              gap: 12,
              overflowX: 'auto',
              paddingBottom: 4,
              paddingRight: 32,
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}>
              {spotlights.map(b => (
                <div key={b.id} style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
                  <PlantSpotlight bed={b} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ data, user, withRail }) {
  const beds = flattenBeds(data.plots);
  const spotlights = pickSpotlights(beds, 4);
  const { today, upcoming } = tasksForToday(data.tasks?.data || data.tasks);
  const visibleTasks = today.length > 0 ? today : upcoming.slice(0, 3);
  const taskCount = today.length;

  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  return (
    <div className="t-stagger" style={{ padding: '32px 40px 60px' }}>
      <div style={{ marginLeft: -40, marginRight: -40, marginTop: -32, marginBottom: 18 }}>
        <EmailVerificationBanner user={user} />
        {data.reminders && <WithdrawalBanner reminders={data.reminders} />}
      </div>
      {!welcomeDismissed && (
        <div style={{ marginBottom: 18 }}>
          <WelcomeCard user={user} onDismiss={() => setWelcomeDismissed(true)} />
        </div>
      )}
      <Meta style={{ marginBottom: 18, color: 'var(--t-sage)' }}>Dziś w ogrodzie</Meta>

      <div style={{
        fontFamily: 'var(--t-display)',
        fontWeight: 300,
        fontSize: 'clamp(56px, 7vw, 88px)',
        lineHeight: 0.92,
        letterSpacing: '-.03em',
        color: 'var(--t-text)',
        textWrap: 'balance',
        marginBottom: 18,
      }}>
        {taskCount === 0 ? (
          <>Dzień <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>bez zadań</span>.</>
        ) : (
          <>{taskCount === 1 ? 'Jedna rzecz' : taskCount === 2 ? 'Dwie rzeczy' : taskCount === 3 ? 'Trzy rzeczy' : `${taskCount} rzeczy`}<br />
            <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>dla ciebie</span>.</>
        )}
      </div>

      <div style={{
        fontSize: 16, lineHeight: 1.5,
        color: 'var(--t-text-dim)', maxWidth: 560,
        marginBottom: 36,
      }}>
        Witamy z powrotem{user ? `, ${user.public_display_name || user.username}` : ''}.
        {data.weather?.weather?.description && ` ${data.weather.weather.description.charAt(0).toUpperCase() + data.weather.weather.description.slice(1)}, ${Math.round(data.weather.weather.temperature)}° w ${data.weather.location?.city || 'Twojej lokalizacji'}.`}
      </div>

      <UpcomingHarvestsBlock harvests={data.upcomingHarvests} />

      <SeasonalActionsBlock />

      {visibleTasks.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14, marginBottom: 36,
        }}>
          {visibleTasks.map((t, i) => <TaskCard key={t.id} task={t} num={i + 1} onComplete={data.refetchAll} />)}
        </div>
      ) : (
        <div style={{ marginBottom: 36 }}>
          <EmptyState
            title="Brak zadań na dziś"
            description="Wszystko zrobione lub jeszcze nic nie zaplanowane."
          />
        </div>
      )}

      {/* Inline weather / spotlights gdy nie ma right rail (tablet) */}
      {!withRail && data.weather && (
        <>
          <Rule label="Pogoda" />
          <div style={{ marginBottom: 32 }}>
            <WeatherBlock weather={data.weather} compact />
          </div>

          {spotlights.length > 0 && (
            <>
              <Rule label="Obserwuj" />
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 14,
              }}>
                {spotlights.map(b => <PlantSpotlight key={b.id} bed={b} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Right rail ──────────────────────────────────────────────────
function RailContent({ data }) {
  const beds = flattenBeds(data.plots);
  const spotlights = pickSpotlights(beds, 5);
  return (
    <>
      <div style={{ marginBottom: 18 }}>
        {data.weather ? (
          <Link to="/pogoda" style={{ textDecoration: 'none', display: 'block' }}>
            <WeatherBlock weather={data.weather} compact={false} />
          </Link>
        ) : <Skeleton height={180} />}
        {data.weatherRecs?.recommendations?.alerts?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <WeatherAlertBanner alerts={data.weatherRecs.recommendations.alerts} />
          </div>
        )}
        <div style={{
          marginTop: 8, textAlign: 'right',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.16em', textTransform: 'uppercase',
        }}>
          <Link to="/pogoda" style={{
            color: 'var(--t-sage)', textDecoration: 'none',
          }}>Pełna prognoza →</Link>
        </div>
      </div>

      <Meta style={{ marginBottom: 12 }}>Obserwuj</Meta>
      {spotlights.length > 0 ? (
        spotlights.map(b => <PlantSpotlight key={b.id} bed={b} compact />)
      ) : (
        <Meta style={{ opacity: 0.6 }}>Brak grządek</Meta>
      )}
    </>
  );
}

// ── Today screen root ────────────────────────────────────────────
export default function Today() {
  const { isPhone, isTablet, isDesktop } = useViewport();

  const { results, loading, errors, refetchAll } = useMultipleApi({
    me:      '/api/auth/me',
    tasks:   '/api/tasks?limit=20',
    plots:   '/api/plots/all-with-details',
    weather: '/api/weather/current',
    weatherRecs: '/api/weather/recommendations',
    reminders: '/api/reminders',
    upcomingHarvests: '/api/harvest/upcoming?limit=5&days=21',
  });

  // Auth error - bardzo specyficzny case (najczęstszy)
  if (errors.me && (errors.me.includes('autoryz') || errors.me.includes('zalog') || errors.me === 'Brak tokenu autoryzacji')) {
    return <Layout active="today"><ErrorState error={{ status: 401, message: errors.me }} /></Layout>;
  }

  if (loading) {
    // Page-level skeleton zamiast central spinner — mniejszy layout shift,
    // user widzi "kształt" strony zanim dane dotrą.
    return <Layout active="today"><TodaySkeleton /></Layout>;
  }

  const user = results.me?.user;
  const data = {
    tasks: results.tasks,
    plots: results.plots,
    weather: results.weather,
    weatherRecs: results.weatherRecs,
    reminders: results.reminders,
    upcomingHarvests: results.upcomingHarvests,
    refetchAll,
  };

  if (isPhone || isTablet) {
    return <Layout active="today"><MobileContent data={data} user={user} /></Layout>;
  }

  return (
    <Layout
      active="today"
      topBar={<TopBar
        left={<Meta>{formatToday()}</Meta>}
        right={
          <div style={{
            padding: '7px 12px', borderRadius: 8,
            background: 'var(--t-bg)',
            border: '1px solid var(--t-line-2)',
            fontSize: 12, color: 'var(--t-text-dim)',
            display: 'flex', alignItems: 'center', gap: 8, minWidth: 220,
          }}>
            <Icons.Sun color="var(--t-text-dim)" size={14} />
            Witaj{user ? `, ${user.public_display_name || user.username}` : ''}
          </div>
        }
      />}
      rightRail={isDesktop ? <RailContent data={data} /> : null}
    >
      <DesktopContent data={data} user={user} withRail={isDesktop} />
    </Layout>
  );
}
